import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Loader2, HeartPulse, TrendingUp, AlertTriangle, Siren, ArrowRight, Activity } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, PriorityBadge, scoreColor } from "@/components/common/ui";
import { cn } from "@/lib/utils";

export default function HealthIntelligence() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [metric, setMetric] = useState("weight");
  const [escalating, setEscalating] = useState(false);

  useEffect(() => {
    api.get("/health/overview").then((r) => { setOverview(r.data); if (r.data.length) setActive(r.data[0]); }).catch(() => setOverview([]));
  }, []);
  useEffect(() => {
    if (active) api.get(`/residents/${active.resident_id}/health`).then((r) => setDetail(r.data)).catch(() => {});
  }, [active]);

  const escalate = async () => {
    setEscalating(true);
    try {
      const { data } = await api.post(`/health/escalate/${active.resident_id}`, { reason: `Health-signal review for ${active.name}` });
      toast.success(data.message || "Escalation routed for clinical review");
    } catch (e) { toast.error("Escalation failed"); } finally { setEscalating(false); }
  };

  if (!overview) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const METRICS = [
    { key: "weight", label: "Weight (lb)" }, { key: "heart_rate", label: "Heart Rate (bpm)" },
    { key: "bp_systolic", label: "Systolic BP" }, { key: "sleep_hours", label: "Sleep (hrs)" },
  ];
  const series = (detail?.vitals || []).map((v) => ({ date: v.date.slice(5), value: v[metric] }));

  return (
    <div>
      <PageHeader overline="Care · Module 12 · Clinical Intelligence" title="Resident Health Intelligence"
        description="Baseline-change detection and risk signals as decision support. Predictions are explainable, human-reviewed and never a diagnosis." />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Watchlist */}
        <div className="lg:col-span-4">
          <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
            <div className="flex items-center gap-2 border-b px-4 py-3 text-sm font-bold"><AlertTriangle size={15} className="text-[#F79009]" /> Health Watchlist</div>
            <div className="careiq-scroll max-h-[560px] divide-y overflow-y-auto">
              {overview.map((o) => (
                <button key={o.resident_id} data-testid={`health-${o.resident_id}`} onClick={() => setActive(o)}
                  className={cn("flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50", active?.resident_id === o.resident_id && "bg-muted/60")}>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{o.name}</div>
                    <div className="truncate text-xs text-muted-foreground">{o.program_name}</div>
                    <div className="mt-1"><PriorityBadge level={o.risk_level} /></div>
                  </div>
                  {o.signal_count > 0 && (
                    <span className="rounded-full bg-[#F0443815] px-2 py-0.5 text-xs font-semibold text-[#F04438]">{o.signal_count} signal{o.signal_count > 1 ? "s" : ""}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-8 space-y-6">
          {active && detail && (
            <>
              <div className="rounded-xl border bg-card p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#155EEF]/10 text-[#155EEF]"><HeartPulse size={22} /></span>
                    <div><div className="text-lg font-bold tracking-tight">{active.name}</div>
                      <div className="text-xs text-muted-foreground">Risk score {active.risk_score} · {active.risk_level}</div></div>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid="view-profile-btn" onClick={() => navigate(`/residents/${active.resident_id}`)}
                      className="flex items-center gap-1 rounded-md border px-3 py-2 text-xs font-medium hover:border-[#155EEF] hover:text-[#155EEF]">Profile <ArrowRight size={13} /></button>
                    <button data-testid="escalate-btn" onClick={escalate} disabled={escalating}
                      className="flex items-center gap-1.5 rounded-md bg-[#F04438] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#D92D20] disabled:opacity-60">
                      {escalating ? <Loader2 size={13} className="animate-spin" /> : <Siren size={13} />} Escalate for Clinical Review
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {METRICS.map((m) => (
                    <button key={m.key} onClick={() => setMetric(m.key)}
                      className={cn("rounded-full border px-3 py-1 text-xs font-medium transition-colors", metric === m.key ? "border-[#155EEF] bg-[#155EEF] text-white" : "bg-background hover:border-[#155EEF]")}>{m.label}</button>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={200} className="mt-3">
                  <LineChart data={series} margin={{ left: -18, right: 8, top: 8, bottom: 0 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
                    <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
                    <Line type="monotone" dataKey="value" stroke="#155EEF" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="rounded-xl border bg-card p-5 shadow-sm" style={{ borderLeft: "3px solid #7C3AED" }}>
                <div className="mb-3 flex items-center gap-2 text-sm font-bold"><TrendingUp size={15} className="text-[#7C3AED]" /> Detected Signals (decision support)</div>
                {detail.signals.length > 0 ? (
                  <div className="space-y-2">
                    {detail.signals.map((s, i) => {
                      const col = s.severity === "critical" ? "#F04438" : "#F79009";
                      return (
                        <div key={i} data-testid={`signal-${i}`} className="flex items-start gap-3 rounded-lg border bg-background p-3">
                          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md" style={{ backgroundColor: col + "1a", color: col }}><Activity size={14} /></span>
                          <div>
                            <div className="text-sm font-semibold">{s.metric}: <span style={{ color: col }}>{s.change}</span></div>
                            <div className="text-xs text-muted-foreground">{s.note}</div>
                          </div>
                        </div>
                      );
                    })}
                    <p className="pt-1 text-xs text-muted-foreground">These are observations, not diagnoses. Use the escalation workflow to route to authorized clinical personnel.</p>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">No significant changes from baseline detected.</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
